import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ActivityIndicator, Button, Surface, Text, useTheme } from 'react-native-paper';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { createSelfPayCheckoutSession } from '@/src/api/apiService';
import { APP_ORIGIN } from '@/src/api/config';
import {
  checkoutDeepLink,
  openNativeStripeCheckout,
  selfPaySettleFromResult,
} from '@/src/features/checkout/nativeStripeCheckout';
import { track, resolveEnvironment } from '@/src/lib/analytics';
import { AnalyticsEvent } from '@/src/lib/analyticsEvents';
import { useAuth } from '@/src/context/AuthContext';
import { usePaywallConfig } from '@/src/hooks/usePaywallConfig';
import { useProductCatalog } from '@/src/hooks/useProductCatalog';
import { usePaywallStatus } from '@/src/hooks/usePaywallStatus';
import { deriveSelfPaySurface } from '@/src/utils/selfPayExit';
import {
  useProductPricingSurface,
  type ProductPricingSelection,
} from '@/src/components/onboarding/useProductPricingSurface';

/**
 * Driver self-pay (#207, #289, #291, #402): product and catalog pricing are
 * chosen on the shared two-phase surface after registration.
 *
 * The screen owns the scroll contract (#291): the phase content scrolls, the
 * total and CTA are pinned. Before, this screen had no ScrollView at all and
 * centred its content, so anything taller than the viewport was clipped at BOTH
 * ends and the CTA could not be reached on a small phone.
 *
 * #402: the surface is now decided by server-authoritative entitlement
 * (`deriveSelfPaySurface`) BEFORE any trial/purchase CTA is exposed, and
 * `Voltar ao início` + canonical `Sair` are present in every state (loading,
 * unknown/error, payment recovery, purchase). A company-funded driver with
 * valid coverage is routed home instead of being invited to buy a second
 * license; an unresolved entitlement fails closed with retry, never a
 * purchasable default.
 */
export default function SelfPayScreen() {
  const { t } = useTranslation('paywall');
  const theme = useTheme();
  const router = useRouter();
  const { logout } = useAuth();

  const {
    status: paywallStatus,
    isLoading: paywallLoading,
    isUnknown: paywallUnknown,
    refetch: refetchPaywallStatus,
    retry: retryPaywallStatus,
  } = usePaywallStatus();
  const {
    catalog,
    isLoading: catalogLoading,
    error: catalogError,
    refetch: refetchCatalog,
  } = useProductCatalog();
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { bottom } = useSafeAreaInsets();

  // While the authoritative status is still in flight the surface is
  // unresolved; once resolved it is one of redirect-home / payment-recovery /
  // fail-closed / purchase. Only `purchase` may expose pricing.
  const surface = paywallLoading
    ? null
    : deriveSelfPaySurface(paywallStatus, paywallUnknown);
  const isPurchase = surface?.kind === 'purchase';
  // Config loading persists experiment allocation; only an eligible purchase
  // surface may perform it, never a covered redirect or unresolved entitlement.
  const { config, isLoading: configLoading } = usePaywallConfig('driver_self_pay', 'hours', isPurchase);

  useEffect(() => {
    if (surface?.kind === 'redirect-home') {
      router.replace('/dashboard/home');
    } else if (surface?.kind === 'payment-recovery') {
      // Existing recovery boundary (#405/paywall-expired): fix the payment
      // method, never start a fresh trial/purchase.
      router.replace('/dashboard/paywall-expired');
    }
  }, [surface?.kind, router]);

  const goToDashboard = useCallback(() => {
    router.replace({ pathname: '/register/driver', params: { review_payer: '1' } });
  }, [router]);

  const signOut = useCallback(async () => {
    await logout();
    router.replace('/');
  }, [logout, router]);

  // Canonical funnel: the self-pay pricing surface is the driver's
  // pricing_viewed step (the product/cycle choice is made here). Only emit once
  // the paywall config has actually loaded AND the surface really is a
  // purchase — emitting before the pricing is visible/decided, or while an
  // entitlement/recovery state should not show pricing, would report a viewed
  // price the driver never saw (#293, #402).
  const pricingViewedRef = useRef(false);
  useEffect(() => {
    if (pricingViewedRef.current) return;
    if (!isPurchase) return;
    if (!config || configLoading) return;
    pricingViewedRef.current = true;
    track(AnalyticsEvent.PricingViewed, {
      user_role: 'driver',
      feature: 'hours',
      billing: 'monthly',
      app_variant: resolveEnvironment(),
    });
  }, [config, configLoading, isPurchase]);

  const pricing = useProductPricingSurface({
    persona: 'driver',
    catalog,
    catalogLoading,
    catalogError,
    onRetryCatalog: () => void refetchCatalog(),
    paywallConfig: config,
    paywallConfigLoading: configLoading,
    confirmTestID: 'self-pay-cta',
    onConfirm: selection => void startCheckout(selection),
  });

  async function startCheckout(selection: ProductPricingSelection) {
    setStarting(true);
    setError(null);
    try {
      const trialDays = config?.plans?.find(plan => plan.stripe_price_id === selection.plan.price_id)?.trial_days;
      const returnUrl = `${APP_ORIGIN}/checkout-return`;
      // #290: on native, tell the server which app deep link Stripe must be
      // handed back to, and watch that exact link below. Web keeps the
      // full-page redirect to the same HTTPS return URL as before.
      const isNative = Platform.OS !== 'web';
      const deepLink = isNative ? checkoutDeepLink('/checkout-return') : null;
      const response = await createSelfPayCheckoutSession({
        return_url: returnUrl,
        feature: selection.feature,
        // #291: the driver may now buy the cycle it was shown. The server
        // re-validates both against the active catalog and rejects a mismatch
        // rather than substituting a price.
        price_id: selection.plan.price_id,
        billing: selection.billing,
        ...(deepLink ? { frontend_return_url: deepLink, is_mobile: true } : {}),
      });
      if (!response.ok || !response.data?.checkout_url) {
        const apiError =
          typeof (response.data as { error?: unknown } | undefined)?.error === 'string'
            ? (response.data as { error: string }).error
            : typeof (response.data as { detail?: unknown } | undefined)?.detail === 'string'
              ? (response.data as { detail: string }).detail
              : null;
        throw new Error(
          apiError || t('selfPay.checkoutError', 'Não foi possível iniciar o pagamento. Tente novamente.'),
        );
      }
      // checkout_started is emitted only AFTER the Stripe checkout session is
      // created — a failed create must not be reported as a started checkout.
      // NOTE: `payment_submitted` is intentionally NOT emitted here. Creating a
      // checkout session is NOT a real post-Stripe payment submission; equating
      // checkout_started with payment_submitted would double-count the funnel.
      // The genuine submission signal arrives from Stripe (webhook /
      // checkout-return), never from this client-side session creation.
      track(AnalyticsEvent.CheckoutStarted, {
        user_role: 'driver',
        feature: selection.feature,
        billing: selection.billing,
        price_id: selection.plan.price_id,
        payer: 'self',
        app_variant: resolveEnvironment(),
      });
      if (Platform.OS === 'web') {
        window.location.assign(response.data.checkout_url);
        return;
      }
      const result = await openNativeStripeCheckout({
        checkoutUrl: response.data.checkout_url,
        deepLink: deepLink!,
      });
      if (result.type === 'success') {
        const settle = selfPaySettleFromResult(result);
        // A malformed callback carries no settle state; the return screen then
        // reads the authoritative paywall status instead of assuming success.
        router.replace(
          settle === 'unknown'
            ? '/checkout-return'
            : { pathname: '/checkout-return', params: { self_pay: settle } },
        );
      } else if (result.type === 'dismiss' || result.type === 'cancel') {
        // The user closed the browser themselves before Stripe redirected.
        await refetchPaywallStatus();
        router.replace('/dashboard/home');
      }
    } catch (e) {
      track('payment_failed', {
        reason: 'self_pay_checkout_error',
        feature: selection.feature,
        user_role: 'driver',
        app_variant: resolveEnvironment(),
      });
      setError(e instanceof Error ? e.message : t('selfPay.checkoutError', 'Não foi possível iniciar o pagamento.'));
    } finally {
      setStarting(false);
    }
  }

  return (
    <Surface style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {/* Exit/recovery contract (#402): the dashboard return and canonical
          logout must be reachable in EVERY state — loading, unknown/error,
          payment recovery and purchase — without relying on the browser/system
          back button, which is unavailable under headerShown: false. */}
      <View style={styles.topBar}>
        <Button
          mode="text"
          compact
          icon="arrow-left"
          onPress={goToDashboard}
          testID="self-pay-exit-dashboard"
          accessibilityRole="button"
        >
          {t('register:back', 'Voltar')}
        </Button>
        <Button
          mode="text"
          compact
          onPress={() => void signOut()}
          testID="self-pay-logout"
          accessibilityRole="button"
        >
          {t('selfPay.exitLogout', 'Sair')}
        </Button>
      </View>

      {!isPurchase ? (
        <View style={styles.gateBody} testID="self-pay-entitlement-gate">
          {surface?.kind === 'fail-closed' ? (
            <>
              <Text
                variant="bodyMedium"
                style={[styles.body, { color: theme.colors.onSurfaceVariant }]}
                testID="self-pay-unknown"
              >
                {t(
                  'selfPay.unknownBody',
                  'Não foi possível confirmar a sua licença. Verifique a ligação e tente novamente.',
                )}
              </Text>
              <Button
                mode="contained"
                onPress={retryPaywallStatus}
                testID="self-pay-retry"
                accessibilityRole="button"
              >
                {t('selfPay.retry', 'Tentar novamente')}
              </Button>
            </>
          ) : (
            <ActivityIndicator accessibilityLabel={t('loading', 'A verificar a sua licença')} />
          )}
        </View>
      ) : (
        <>
          <ScrollView
            style={styles.scroll}
            contentContainerStyle={styles.content}
            keyboardShouldPersistTaps="handled"
            testID="self-pay-scroll"
          >
            <Text
              variant="headlineSmall"
              style={[styles.title, { color: theme.colors.primary }]}
              accessibilityRole="header"
              testID="self-pay-title"
            >
              {t('selfPay.title', 'Pague a sua própria licença')}
            </Text>
            <Text variant="bodyMedium" style={[styles.body, { color: theme.colors.onSurfaceVariant }]}>
              {t(
                'selfPay.body',
                'Continue a conduzir com o AllDrivers mesmo que a sua empresa deixe de pagar — a licença fica sua, associada à sua conta.',
              )}
            </Text>

            {starting ? <ActivityIndicator testID="self-pay-starting" /> : pricing.content}

            {error && (
              <Text style={[styles.error, { color: theme.colors.error }]} accessibilityRole="alert">
                {error}
              </Text>
            )}
          </ScrollView>
          {!starting && pricing.footer ? (
            <View style={[styles.footer, { paddingBottom: 16 + bottom }]}>{pricing.footer}</View>
          ) : null}
        </>
      )}
    </Surface>
  );
}

const styles = StyleSheet.create({
  // No `justifyContent: 'center'` here: it clipped overflowing content at both
  // ends instead of letting it scroll (#291).
  container: { flex: 1 },
  topBar: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 8,
    paddingTop: 8,
  },
  gateBody: { alignItems: 'center', flex: 1, gap: 16, justifyContent: 'center', padding: 24 },
  scroll: { flex: 1 },
  content: { alignSelf: 'center', gap: 8, maxWidth: 560, padding: 24, paddingBottom: 8, width: '100%' },
  footer: { alignSelf: 'center', maxWidth: 560, paddingHorizontal: 24, paddingTop: 8, width: '100%' },
  title: { marginBottom: 12, textAlign: 'center' },
  body: { marginBottom: 24, textAlign: 'center' },
  error: { marginTop: 16, textAlign: 'center' },
});
